import {html} from 'lit-html';
import {repeat} from 'lit-html/directives/repeat';
import {classMap} from 'lit-html/directives/class-map';
import {styleMap} from 'lit-html/directives/style-map';
import {ifDefined} from 'lit-html/directives/if-defined';

import {cdnImg} from '../shopify-cdn';

/**
 *
 * Render a Product gallery
 *
 * @returns {TemplateResult}
 */

const videoTemplate = (media) => {

    // shopify docs: https://shopify.dev/themes/product-merchandising/media/support-media

    // NEW: model viewer & AR support
    if(media.media_type === 'model') {
        return modelTemplate(media);
    }

    // default autoplay via <script>window.clGalleryAutoplay = true;</script>
    var autoPlay = typeof window.clGalleryAutoplay !== 'undefined' ? window.clGalleryAutoplay : false;
    // dynamic responsive embeds via aspect ratio
    var style = { paddingBottom: ((1 / media.aspect_ratio) * 100) + '%' };

    // INFO: lazy loading not implemented - but we set loading="lazy" attribute
    if(media.media_type === 'external_video'){
        if(media.host === 'youtube') {
            // INFO: autoplay seems to not work - but url is correct - some strange js error on load
            return html`<div class="cl-embed" style=${styleMap(style)}><iframe src="https://www.youtube.com/embed/${media.external_id}?controls=1&amp;enablejsapi=1&amp;modestbranding=1&amp;playsinline=1&amp;rel=0&amp;autoplay=${autoPlay ? '1' : '0'}" loading="lazy" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen="allowfullscreen"></iframe></div>`;
        } else if(media.host === 'vimeo'){
            return html`<div class="cl-embed" style=${styleMap(style)}><iframe src="https://player.vimeo.com/video/${media.external_id}?byline=0&amp;controls=1&amp;playsinline=1&amp;title=0" loading="lazy" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen="allowfullscreen" ></iframe></div>`;
        }
    }

    // INFO: we add the biggest variant for each mime type (sources return different sizes with same mime type)
    var sources = [];
    var mimeTypes = [];
    // traverse backwards to get the largest size for each mime type
    for (var i = media.sources.length - 1; i >= 0; i--) {
        var source = media.sources[i];
        if(mimeTypes.indexOf(source.mime_type) === -1){
            sources.push(source);
            mimeTypes.push(source.mime_type);
        }
    }

    // INFO: preload="none" simulate LAZY loading     autoPlay only works with muted flag!  implicit enable loop
    return html`<div class="cl-embed" style=${styleMap(style)}><video autoplay=${ifDefined(autoPlay ? 'autoplay': undefined)} loop=${ifDefined(autoPlay ? 'loop': undefined)} muted="muted" playsinline="playsinline" controls="controls" preload="none" poster="${media.preview_image.src}">
        ${repeat(sources, (source) => html`
            <source src="${source.url}" type="${source.mime_type}">
        `)}
    </video></div>`
}

const modelTemplate = (media) => {
    if (window.Shopify) {

        function setupShopifyXr() {
            // IMPORTANT: timeout to wait for gallery to render before we setup the XR models
            setTimeout(function(){
                window.ShopifyXR.addModels([ media ]);
                window.ShopifyXR.setupXRElements();
            }, 1000);
        }

        if (!window.ShopifyXR) {
            // load XR lib if missing
            window.Shopify.loadFeatures([{name: 'shopify-xr', version: '1.0', onLoad: setupShopifyXr}]);
        } else {
            setupShopifyXr();
        }
    }

    var source;
    for (var i = media.sources.length - 1; i >= 0; i--) {
        source = media.sources[i];
        // default format glb (not sure if other formats are supported but this works)
        if(source.format === 'glb'){
            break;
        }
    }

    // <model-viewer> needs to be loaded by theme (most new themes)
    return html`<div class="cl-embed" style="padding-top: 100%;"><model-viewer toggleable="true" src=${source.url} poster=${media.preview_image.src} camera-controls="true" style="--poster-color: transparent;" data-shopify-feature="1.12"></model-viewer></div>
        ${window.clGalleryXR !== false ? 
            html`<button class="button button--full-width product__xr-button cl-xr-button" type="button" data-shopify-xr="" data-shopify-title="Model" data-shopify-model3d-id=${media.id} data-shopify-xr-hidden="">
                <svg xmlns="http://www.w3.org/2000/svg" focusable="false" fill="none" viewBox="0 0 18 21"><path d="M7.67998 20.629L1.28002 16.723C0.886205 16.4784 0.561675 16.1368 0.337572 15.731C0.113468 15.3251 -0.00274623 14.8686 -1.39464e-05 14.405V6.59497C-0.00238367 6.13167 0.113819 5.6755 0.33751 5.26978C0.561202 4.86405 0.884959 4.52227 1.278 4.27698L7.67796 0.377014C8.07524 0.131403 8.53292 0.000877102 8.99999 9.73346e-08C9.46678 -0.000129605 9.92446 0.129369 10.322 0.374024V0.374024L16.722 4.27399C17.1163 4.51985 17.4409 4.86287 17.6647 5.27014C17.8885 5.67742 18.0039 6.13529 18 6.59998V14.409C18.0026 14.8725 17.8864 15.3289 17.6625 15.7347C17.4386 16.1405 17.1145 16.4821 16.721 16.727L10.321 20.633C9.92264 20.8742 9.46565 21.0012 8.99999 21C8.53428 20.9998 8.07761 20.8714 7.67998 20.629V20.629ZM8.72398 2.078L2.32396 5.97803C2.22303 6.04453 2.14066 6.13551 2.08452 6.24255C2.02838 6.34959 2.00031 6.46919 2.00298 6.59003V14.4C2.00026 14.5205 2.02818 14.6396 2.08415 14.7463C2.14013 14.853 2.22233 14.9438 2.32298 15.01L7.99999 18.48V10.919C8.00113 10.5997 8.08851 10.2867 8.25292 10.0129C8.41732 9.73922 8.65267 9.51501 8.93401 9.36401L15.446 5.841L9.28001 2.08002C9.19614 2.02738 9.09901 1.99962 8.99999 2C8.90251 1.99972 8.8069 2.02674 8.72398 2.078V2.078Z" fill="currentColor"></path></svg>
                ${window.clGalleryXRText || 'View in your space'}
            </button>` : null
        }`;
}

const sizeGalleryImage = (url, config) => {

    if (config.width > 0) {

        // scale up to minimum 2x retina size
        const devicePixelRatio = Math.max(window.devicePixelRatio || 1, 2);
        let size = config.width * devicePixelRatio;
        // round to next 200
        size = Math.ceil(size / 200) * 200;

        size = Math.min(size, config.maxWidth); // max image

        return cdnImg(url, size + 'x');
    }

    return url;
}


const template = (media, config) => html`
<div class="cl-po--gallery ${config.layout}">

    <div class="splide cl-po--gallery-main  ${classMap({'hide-thumb-mobile': config.hideThumbnailsMobile })}">
        <div class="splide__track">
            <div class="splide__list">
                ${repeat(media, (media) => media.id, (media,mediaIndex) => html`
                    <div class="splide__slide" style=${styleMap({aspectRatio: media.aspect_ratio /* NEW: aspect ratio from media for lazy loaded img (reset after img is loaded) */ })}>
                        ${media.media_type === 'video' || media.media_type === 'external_video' || media.media_type === 'model' ? html`<div class="cl-po--gallery-img" data-media-position=${media.position} >${videoTemplate(media)}</div>` :
                                html`<img class="cl-po--gallery-img" data-splide-lazy=${sizeGalleryImage(media.src, config)} alt=${media.alt} data-media-position=${media.position} />`  
                        }
                    </div>
                `)}
            </div>
        </div>
    </div>
    ${!config.hideThumbnails ? html`
        <div class="splide cl-po--gallery-thumbnails ${classMap({'cl-hide-mobile': config.hideThumbnailsMobile })}">
            <div class="splide__track">
                <div class="splide__list">
                    ${repeat(media, (media) => media.id, (media,mediaIndex) => html`
                    <div class="splide__slide">
                        <img class="cl-po--gallery-thumbnail" data-splide-lazy=${cdnImg(media.src || media.preview_image.src, '200x')} alt=${media.alt} data-media-position=${media.position} />
                    </div>
                `)}
                </div>
            </div>
        </div>
    `: null}
</div>
`;


export default template;